import * as yup from "yup";
import { FilterDefinition } from "components/Shared/FilterEditor/utils";
import {
  createMakeGetManyFilterResult,
  getBooleanFilterDefinition,
  getDateTimeFilterDefinition,
  getSearchFilterDefinition,
  getSelectFilterDefinition,
  getStringSelectFilterDefinition,
  notStringFilterVariants,
} from "components/Shared/FilterEditor/standardFilters";
import {
  OrderingPlatformFilter,
  OrderingPlatformType,
} from "__generated__/gql/graphql";
import OrderingPlatformSelect from "components/Shared/Select/OrderingPlatformSelect";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";
import {
  createNestedAddressFilterDefinitions,
  FULL_ADDRESS_FILTER_ID,
} from "components/Shared/FilterEditor/addressFilterDefinitions";
import { INCLUDE_CUSTOM_FILTER_NAME } from "components/Shared/IncludeCustomFilter";
import { makeEnsureType } from "utils/typescriptUtilis";
import {
  getIsEveryOptionSelected,
  ORDERING_PLATFORM_TYPE_LABELS,
} from "components/Shared/OrderingPlatformDirectory/OrderingPlatformTypeBadge";
import EnumSelect from "components/Shared/Select/EnumSelect";
import { getAssertedArrayOfType, makeAssertIsEnum } from "utils/assertions";
import { getEnumSchema } from "utils/schemas/utils";
import RetailerSelect from "components/Shared/Select/RetailerSelect";

const filterObj = makeEnsureType<OrderingPlatformFilter>();

const makeGetManyResult =
  createMakeGetManyFilterResult<OrderingPlatformFilter>();

const DC_ADDRESS_PREFIX = "distributionCenter__address";

const dcAddressFilterDefs = createNestedAddressFilterDefinitions(
  DC_ADDRESS_PREFIX,
  {
    groupLabel: "Distribution Center",
    getNestedResult: (value, context) => {
      const {
        filterDefinition: { id: defId },
        filterVariantDefinition: { id: variantId },
      } = context;

      const inversedVariantId = notStringFilterVariants[variantId];

      const fieldName =
        defId === DC_ADDRESS_PREFIX
          ? FULL_ADDRESS_FILTER_ID
          : defId.replace(`${DC_ADDRESS_PREFIX}__`, "");

      return makeGetManyResult("distributionCenters", () => ({
        address: {
          [fieldName]: {
            [inversedVariantId || variantId]: value,
          },
        },
      }))(value, context);
    },
  },
);

export const ORDERING_PLATFORM_TYPE_FILTER_ID = "orderingPlatformType";

export function getFilterDefinitionsWithoutDc(
  isAdmin?: boolean,
): FilterDefinition[] {
  const defs: FilterDefinition[] = [
    getSearchFilterDefinition({
      getResult: (search) => ({
        or: [
          { name: { includesInsensitive: search } },
          {
            distributionCenters: {
              some: { name: { includesInsensitive: search } },
            },
          },
        ],
      }),
    }),
    getStringSelectFilterDefinition({
      id: "orderingPlatform",
      label: "Customer Name",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) => ({
        name: { [variantId]: value },
      }),
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => ({ id: { [variantId]: value } }),
      schema: yup.string().min(2),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <OrderingPlatformSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getSelectFilterDefinition({
      id: ORDERING_PLATFORM_TYPE_FILTER_ID,
      label: "Customer Type",
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => {
        const orderingPlatformTypes = getAssertedArrayOfType(
          value,
          makeAssertIsEnum<OrderingPlatformType>(OrderingPlatformType),
        );
        if (getIsEveryOptionSelected(orderingPlatformTypes)) return;
        return filterObj({
          orderingPlatformType: { [variantId]: orderingPlatformTypes },
        });
      },
      selectSchema: getEnumSchema<OrderingPlatformType>(OrderingPlatformType),
      renderSelectInput: ({ field }, { isMulti }) => (
        <EnumSelect
          {...field}
          enum={OrderingPlatformType}
          labelsByValue={ORDERING_PLATFORM_TYPE_LABELS}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getStringSelectFilterDefinition({
      id: "directRetailer",
      label: "Direct Retailer",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({
          directRetailer: { name: { [variantId]: value } },
        }),
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => filterObj({ directRetailerId: { [variantId]: value } }),
      schema: yup.string().min(2),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <RetailerSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          filter={{ orderingPlatformByDirectRetailerIdExists: true }}
        />
      ),
    }),
    getBooleanFilterDefinition({
      id: "isParent",
      label: "Customer is Parent?",
    }),
    getStringSelectFilterDefinition({
      id: "parent",
      label: "Parent Customer",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({
          parent: { name: { [variantId]: value } },
        }),
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => filterObj({ parentId: { [variantId]: value } }),
      schema: yup.string().min(2),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <OrderingPlatformSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          filter={{ isParent: { equalTo: true } }}
        />
      ),
    }),
    getBooleanFilterDefinition({
      id: "isCustom",
      label: "Custom?",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        ["equalTo", "notEqualTo"].includes(variantId)
          ? { forBrandId: { isNull: variantId === "equalTo" ? !value : value } }
          : undefined,
    }),
    getBooleanFilterDefinition({
      id: INCLUDE_CUSTOM_FILTER_NAME,
      label: "Include Custom?",
      isHidden: true,
      getResult: (value, { filterVariantDefinition: { id: variantId } }) => {
        if (
          (variantId === "equalTo" && value) ||
          (variantId === "notEqualTo" && !value)
        ) {
          return;
        }
        return filterObj({ forBrandId: { isNull: true } });
      },
    }),
  ];

  if (isAdmin) {
    defs.push(
      getDateTimeFilterDefinition({
        id: "createdAt",
        label: "Created at",
        defaultVariant: "greaterThan",
      }),
    );
  }

  return defs;
}

export default function getFilterDefinitions(
  isAdmin?: boolean,
): FilterDefinition[] {
  return [
    ...getFilterDefinitionsWithoutDc(isAdmin),
    getStringSelectFilterDefinition({
      id: "distributionCenter",
      label: "Distribution Center",
      getResult: makeGetManyResult("distributionCenters", (f) => ({ name: f })),
      getSelectResult: makeGetManyResult("distributionCenters", (f) => ({
        id: f,
      })),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <DistributionCenterSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          filter={{ name: { isNull: false } }}
        />
      ),
    }),
    ...dcAddressFilterDefs,
  ];
}
